import React, { useState, useEffect } from 'react';
import {
  BarChart3, CheckCircle2, Clock, AlertTriangle, TrendingUp,
  Calendar, Users, ListTodo, Star, Sun, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import { cn } from '../../lib/utils';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

export default function TasksDashboard() {
  const [stats, setStats] = useState(null);
  const [productivity, setProductivity] = useState(null);
  const [workload, setWorkload] = useState(null);
  const [byList, setByList] = useState(null);
  const [period, setPeriod] = useState('week');
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashRes, prodRes, workRes, listRes] = await Promise.all([
        fetch(`${API_URL}/api/task-reports/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/task-reports/productivity?period=${period}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/task-reports/workload`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/task-reports/by-list`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      const [dashData, prodData, workData, listData] = await Promise.all([
        dashRes.json(),
        prodRes.json(),
        workRes.json(),
        listRes.json()
      ]);

      setStats(dashData);
      setProductivity(prodData);
      setWorkload(workData);
      setByList(listData);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [period]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const statusData = stats ? [
    { name: 'À faire', value: stats.by_status?.todo || 0, color: '#6B7280' },
    { name: 'En cours', value: stats.by_status?.in_progress || 0, color: '#3B82F6' },
    { name: 'Terminé', value: stats.by_status?.done || 0, color: '#10B981' }
  ] : [];

  const priorityData = stats ? [
    { name: 'Critique', value: stats.by_priority?.critical || 0, color: '#EF4444' },
    { name: 'Haute', value: stats.by_priority?.high || 0, color: '#F59E0B' },
    { name: 'Normale', value: stats.by_priority?.normal || 0, color: '#3B82F6' },
    { name: 'Basse', value: stats.by_priority?.low || 0, color: '#6B7280' }
  ] : [];

  return (
    <div className="h-full overflow-auto bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <BarChart3 className="h-7 w-7 text-blue-500" />
              Tableau de bord
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              Vue d'ensemble de vos tâches
            </p>
          </div>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Cette semaine</SelectItem>
              <SelectItem value="month">Ce mois</SelectItem>
              <SelectItem value="year">Cette année</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Tâches en cours"
            value={stats?.pending || 0}
            icon={ListTodo}
            color="blue"
            subtitle={`${stats?.completion_rate || 0}% complété`}
          />
          <StatCard
            title="À faire aujourd'hui"
            value={stats?.due_today || 0}
            icon={Sun}
            color="yellow"
            subtitle={`${stats?.my_day_count || 0} dans Ma journée`}
          />
          <StatCard
            title="En retard"
            value={stats?.overdue || 0}
            icon={AlertTriangle}
            color="red"
            subtitle="Nécessite attention"
          />
          <StatCard
            title="Terminé cette semaine"
            value={stats?.completed_this_week || 0}
            icon={CheckCircle2}
            color="green"
            subtitle={`${productivity?.average_per_day || 0}/jour en moyenne`}
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Productivity Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-blue-500" />
                Productivité
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={productivity?.data || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return `${date.getDate()}/${date.getMonth() + 1}`;
                      }}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      labelFormatter={(value) => new Date(value).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' })}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="completed" 
                      stroke="#3B82F6" 
                      strokeWidth={2}
                      name="Tâches terminées"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Status Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                Répartition par statut
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64 flex items-center justify-center">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* By List */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Par liste</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {byList?.lists?.slice(0, 5).map((list) => (
                  <div key={list.list_id}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-sm"
                          style={{ backgroundColor: list.color }}
                        />
                        <span className="text-sm font-medium">{list.name}</span>
                      </div>
                      <span className="text-sm text-gray-500">
                        {list.completed}/{list.total}
                      </span>
                    </div>
                    <Progress value={list.completion_rate} className="h-2" />
                  </div>
                ))}
                {(!byList?.lists || byList.lists.length === 0) && (
                  <p className="text-sm text-gray-500 text-center py-4">Aucune liste</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Workload */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-5 w-5 text-purple-500" />
                Charge de travail
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {workload?.users?.map((user) => (
                  <div key={user.user_id} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-sm font-medium">
                        {user.name?.charAt(0).toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{user.name}</p>
                        <p className="text-xs text-gray-500">
                          {user.pending_tasks} tâches en cours
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {user.high_priority > 0 && (
                        <span className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded">
                          {user.high_priority} priorité haute
                        </span>
                      )}
                      {user.overdue > 0 && (
                        <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded">
                          {user.overdue} en retard
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                {(!workload?.users || workload.users.length === 0) && (
                  <p className="text-sm text-gray-500 text-center py-4">Aucune donnée</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, icon: Icon, color, subtitle }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    green: 'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    yellow: 'bg-yellow-50 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400',
    red: 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400'
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <div className={cn("p-3 rounded-lg", colorClasses[color])}>
            <Icon className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
            {subtitle && (
              <p className="text-xs text-gray-400">{subtitle}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
